import { useEffect, useState } from "react";
import Modal from "../../components/Modal";
import AddProvider from "./components/AddProvider";
import AddCompany from "./components/AddCompany";
import AddPlaceOfBusiness from "./components/AddPlaceOfBusiness";
import {
    Accordion,
    Divider,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Text,
    VStack
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { NewProduct } from "./components/Product";
import SearchProductAfterBarCode from "../../components/Modal/SearchProductAfterBarCode";
import ProductInventoryEntry, { ProductWithPriceQuantity } from "./components/ProductInventoryEntry";
import { getAxiosInstance } from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import SearchProductAfterName from "../../components/Modal/SearchProductAfterName";
import SearchProductAfterSku from "../../components/Modal/SearchProductAfterSKU";

/**
 *  X 1. Select or create a provider
 *  X 2. Select company
 *  X 3. Select place of business
 *  X 4. Add Bill number
 *  X 5. Add Bill Date
 *  X 6. Add product by scanning or searching
 *  X 7. Add new product
 *  X 8. Create the new bill and inventory entry
 */

interface WithPriceAndQuantity {
    price: number;
    quantity: number;
}

export interface NewProductWithPriceQuantity extends WithPriceAndQuantity {
    product: NewProduct;
    mainCategory?: string;
    subCategories: string[];
}

interface AddInventoryForm {
    open: boolean;
    onClose: () => void;
}

const AddInventoryForm = (props: AddInventoryForm) => {
    const { open, onClose } = props;

    const { t } = useTranslation("translations");

    const [provider, setProvider] = useState<string>("");
    const [billNo, setBillNo] = useState<string>("");
    const [billDate, setBillDate] = useState<string>(dayjs(new Date()).format("YYYY-MM-DD"));
    const [company, setCompany] = useState<string>("");
    const [placeOfBusiness, setPlaceOfBusiness] = useState<string>("");
    const [unitOfMeasurement, setUnitOfMeasurement] = useState<{ name: string, id: string }[]>([]);

    const [newProducts, setNewProducts] = useState<NewProductWithPriceQuantity[]>([]);
    const [products, setProducts] = useState<ProductWithPriceQuantity[]>([]);

    const deleteNewProduct = (index: number) => {
        const tmp = [...newProducts];
        tmp.splice(index, 1);
        setNewProducts(tmp);
    };

    const deleteProduct = (index: number) => {
        const tmp = [...products];
        tmp.splice(index, 1);
        setProducts(tmp);
    };

    const onEditNewProduct = (index: number, data: NewProductWithPriceQuantity) => {
        const tmp = [...newProducts];
        tmp[index] = data;
        setNewProducts(tmp);
    }

    const onEditProduct = (index: number, data: ProductWithPriceQuantity) => {
        const tmp = [...products];
        tmp[index] = data;
        setProducts(tmp);
    }

    const getUnitsOfMeasure = async () => {
        try {
            const res = await getAxiosInstance().get("/UnitsOfMeasure");
            if (res.status === 200) {
                setUnitOfMeasurement(res.data);
            }
        } catch (error) {
            toast.error("Could not retrieve measurement units", { position: toast.POSITION.BOTTOM_RIGHT });
        }
    }

    const create = async () => {
        try {
            await getAxiosInstance().post('/Inventory', {
                placeOfBusinessId: placeOfBusiness,
                companyId: company,
                providerId: provider,
                billNo,
                billDate: billDate,
                products: products.map((x) => ({
                    id: x.id,
                    price: x.price,
                    quantity: x.quantity,
                    hasSerialNumber: x.product.hasSerialNumber,
                    serialNumber: x.product.serialNumbers,
                })),
                newProducts: newProducts.map((x) => ({
                    product: {
                        name: x.product.name,
                        sku: x.product.sku,
                        unitMeasurementId: x.product.unitMeasurementId,
                        providerIds: [provider],
                        categoriesIds: []
                    },
                    quantity: x.quantity,
                    price: x.price,
                    barcode: x.product.barcode,
                    hasSerialNumber: x.product.hasSerialNumber,
                    serialNumber: x.product.serialNumbers,
                    categories: [x.mainCategory, ...x.subCategories]
                }))
            });
            toast.success(t("INVENTORY_ENTRY_CREATED_SUCCESSFULLY"), { position: "bottom-right" })
            props.onClose();
        } catch (e) {
            toast.error(t("CANT_CREATE_THE_INVENTORY_ENTRY_SERVER_ERROR"), { position: "bottom-right" })
        }
    }

    useEffect(() => {
        getUnitsOfMeasure();
    }, []);

    return (
        <Modal
            header={(t("ADD_INVENTORY_ENTRY_MODAL"))}
            isOpen={open}
            onClose={onClose}
            secondaryActionName={t("CREATE_BILL")}
            secondaryActionEnabled={true}
            secondaryAction={() => create()}
            size={"2xl"}
        >
            <div>
                <AddProvider
                    selectedProvider={provider}
                    setSelectedProvider={setProvider}
                />
                <HStack>
                    <FormControl>
                        <FormLabel>{t("BILL_NO")}</FormLabel>
                        <Input
                            isRequired
                            name='bill_no'
                            value={billNo}
                            onChange={(e) => setBillNo(e.target.value)}
                            placeholder={t("BILL_NO_INPUT_PLACEHOLDER")}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel>{t("BILL_DATE")}</FormLabel>
                        <Input
                            isRequired
                            type="date"
                            name='bill_date'
                            value={billDate}
                            onChange={(e) => setBillDate(e.target.value)}
                            placeholder={t("BILL_DATE_INPUT_PLACEHOLDER")}
                        />
                    </FormControl>
                </HStack>

                <AddCompany
                    selectedCompany={company}
                    setSelectedCompany={setCompany}
                />
                <AddPlaceOfBusiness
                    companyId={company}
                    selectedPlaceOfBusiness={placeOfBusiness}
                    setSelectedPlaceOfBusiness={setPlaceOfBusiness}
                />

                <SearchProductAfterBarCode
                    addNewProduct={(data) => {
                        setNewProducts([
                            ...newProducts,
                            {
                                price: 0,
                                quantity: 1,
                                product: data,
                                mainCategory: '',
                                subCategories: []
                            }
                        ])
                    }}
                    addProduct={(data) => {
                        setProducts([
                            ...products,
                            {
                                id: data.id,
                                price: 0,
                                quantity: 1,
                                product: data
                            }
                        ])
                    }}
                />
                <SearchProductAfterName
                    addProduct={(data) => {
                        setProducts([
                            ...products,
                            {
                                id: data.id,
                                price: 0,
                                quantity: 1,
                                product: data
                            }
                        ])
                    }}
                />
                <SearchProductAfterSku
                    addProduct={(data) => {
                        setProducts([
                            ...products,
                            {
                                id: data.id,
                                price: 0,
                                quantity: 1,
                                product: data
                            }
                        ])
                    }}
                />

                <VStack align="start">
                    <Text fontWeight="semibold" className="mt-4" size="md">{t("PRODUCTS_INPUT_INVENTORY_FORM")}</Text>
                    <Divider />
                </VStack>
                <Accordion>
                    {
                        products.map((product, index) =>
                            <ProductInventoryEntry
                                data={product}
                                disableProduct={true}
                                delete={() => deleteProduct(index)}
                                setData={(d) => onEditProduct(index, d as ProductWithPriceQuantity)}
                                unitOfMeasurement={unitOfMeasurement}
                                providerId={provider}
                            />
                        )
                    }
                </Accordion>

                <VStack align="start">
                    <Text fontWeight="semibold" className="mt-4" size="md">{t("NEW_PRODUCTS_INPUT_INVENTORY_FORM")}</Text>
                    <Divider />
                </VStack>
                <Accordion>
                    {
                        newProducts.map((product, index) =>
                            <ProductInventoryEntry
                                data={product}
                                delete={() => deleteNewProduct(index)}
                                setData={(d) => onEditNewProduct(index, d as NewProductWithPriceQuantity)}
                                unitOfMeasurement={unitOfMeasurement}
                            />
                        )
                    }
                </Accordion>
            </div>
        </Modal>
    );
};

export default AddInventoryForm;
